

import IMapProperty from './IMapProperty';
import BaseDto from './IBaseDto';

function _addMappingsToObject<T>(mappings: Array<IMapProperty>, sourceObject: Object, destObject: T) {
    if ( !sourceObject ) { return; }

    for ( const property of mappings ) {
        let newValue: any = (destObject as any)[property.localName] ?? null;
        const externalName = property.externalName;

        if ( property.removeValueOnImport ?? false ) {
            /* ignore */
            continue;
        } else if ( property.customImport ) {
            newValue = property.customImport(sourceObject)
        } else if ( sourceObject.hasOwnProperty(externalName) ) { 
            newValue = (sourceObject as any)[externalName] as (typeof property.defaultValue);
        } else if ( property.defaultValue != null && !newValue ) {
            newValue = property.defaultValue as (typeof property.defaultValue);
        }
        
        (destObject as any)[property.localName] = newValue;
    }    
}

function create<T extends BaseDto>(mappings: Array<IMapProperty>, sourceObject?: Object, entityName: string = 'BaseDto'): T {
    let newEntity = <T>{};
    newEntity.apply = () => { return apply(mappings, newEntity); };
    newEntity.flatten = () => { return flatten(newEntity, mappings) };
    newEntity.mappings = () => { return mappings; }

    if ( sourceObject ) {
        _addMappingsToObject<T>(mappings, sourceObject, newEntity);
    }
    
    return newEntity;
}

/** take an incoming IMapProperty[] and generate a new object with the sourceObject  */
function apply<T extends BaseDto>(mappings: Array<IMapProperty>, sourceObject: Object): T {
    let newEntity = create<T>(mappings);

    /* if we are given an existing entity, flatten it out & apply that */
    if ( sourceObject.hasOwnProperty('flatten') ) {
        const sourceEntity = sourceObject as BaseDto;
        _addMappingsToObject(mappings, sourceEntity.flatten(), newEntity);
    } else {
        _addMappingsToObject(mappings, sourceObject, newEntity);
    }

    return newEntity;
}

function flatten<T extends BaseDto>(entity: Object, mappings?: Array<IMapProperty>): {} {
    let flattenObject = {};

    for ( const property of mappings ?? [] ) {
        let value = (property as any)['defaultValue'];
        const keySave = (property as any)['externalName'];
        const keyGet = (property as any)['localName'];

        if ( property.removeValueOnExport ?? false ) {
            /* ignore */
            continue;
        } else if ( property.customExport ) {
            value = property.customExport(entity);
        } else if ( entity.hasOwnProperty(keyGet) ) {
            value = (entity as any)[keyGet];
        }

        (flattenObject as any)[keySave] = value;
    }

    return flattenObject;
}

export default {
    create,
    apply,
    flatten,
}